import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore, bindActionCreators } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCartContainer from './Containers/MiniCart.container';
import QuickSearchContainer from './Containers/QuickSearch.container';
import OrganizationSearchContainer from './Containers/OrganizationSearch.container';
import NavigationContainer from './Containers/Navigation.container';
import FacetedSearchContainer from './Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './Containers/FacetedSearchCompact.container';
import NotificationContainer from './Containers/Notification.container';
import DynamicComponent from './Components/DynamicComponent';
import { customScripts } from './wooscripts/scripts';
import { pushToDatalayer } from './wooscripts/gtmEnchanced';
import { filterCustomScripts } from './wooscripts/filter-scripts';
import { add as addToWishList } from './Actions/WishList.action';
import { translate } from './Services/translation';
import { stickyRow } from './wooscripts/stickyRow';
import { openTab } from './wooscripts/OpenTab';
import { changeLayout } from './wooscripts/changeLayout';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = (window.__litium.store =
  window.__litium.store ||
  (window.parent.__litium && window.parent.__litium.store) ||
  createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk, historyMiddleware)),
  ));

window.__litium = {
  ...window.__litium,
  reduxActions: bindActionCreators({ addToWishList } , store.dispatch),
  events: {
    onAddToWishListButtonClick:(sourceDomNode, articleNumber, quantityFieldId = null) => {
      const nodeIdToShowNotification = sourceDomNode.id = Date.now();
      window.__litium.reduxActions.addToWishList({
          articleNumber,
          quantity: quantityFieldId ? document.getElementById(quantityFieldId).value : 1,
          nodeIdToShowNotification,
          notificationMessage: translate('tooltip.addedtowishlist'),

          hash: Date.now(),
      });
      },
  },

  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach(container => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  if (document.getElementById('miniCart')) {
    renderReact(
      <Provider store={store}>
        <MiniCartContainer />
      </Provider>,
      document.getElementById('miniCart'),
    );
  }
  if (document.getElementById('miniCart-mobile')) {
    renderReact(
      <Provider store={store}>
        <MiniCartContainer />
      </Provider>,
      document.getElementById('miniCart-mobile'),
    );
  }
  if (document.getElementById('quickSearch')) {
    renderReact(
      <Provider store={store}>
        <QuickSearchContainer />
      </Provider>,
      document.getElementById('quickSearch'),
    );
  }
  if (document.getElementById('organizationSearch')) {
    renderReact(
      <Provider store={store}>
        <OrganizationSearchContainer />
      </Provider>,
      document.getElementById('organizationSearch'),
    );
  }
  if (document.getElementById('navbar')) {
    renderReact(
      <Provider store={store}>
        <NavigationContainer />
      </Provider>,
      document.getElementById('navbar'),
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchContainer />
      </Provider>,
      document.getElementById('facetedSearch'),
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact'),
    );
  }
  let categorySliders = document.querySelectorAll('.category-slider');
  if (categorySliders.length) {
      const CategorySlider = DynamicComponent({
                  loader: () => import('./components/CategorySlider'),
              });
      Array.from(categorySliders).forEach((categorySlider, index) => {
        const values = [...categorySlider.querySelectorAll('template')].map(
            (block) => {
                return {
                    html: block.innerHTML,
                };
            }
        );
        if (values.length > 0) {
          renderReact(<CategorySlider values={values} />, categorySlider);
        }
      }
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Containers/PersonList.container'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons'),
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Containers/AddressList.container'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses'),
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Containers/Checkout.container'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout'),
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Containers/LightboxImages.container'),
    });
    const rootElement = document.getElementById('lightBoxImages');
    const media = Array.from(rootElement.querySelectorAll('a media'));
    renderReact(
        <Provider store={store}>
            <LightboxImages media={media}/>
        </Provider>,
        document.getElementById('lightBoxImages'),
    );
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('.slider__block')].map(
          block => {
            return {
              image: block.dataset.image,
              mobileimage: block.dataset.mobileimage,
              url: block.dataset.url,
              text: block.dataset.text,
              color: block.dataset.color,
              textsection: block.dataset.textsection,
              textplacement: block.dataset.textplacement,
              textsize: block.dataset.textsize,
              texturl: block.dataset.texturl,
              buttonseparated: block.dataset.buttonseparated,
            };
          },
        );
        if (values.length > 0) {
          renderReact(<Slider values={values} autoPlay={slider.dataset.autoplay === "True"} />, slider);
        }
      },
    );
  }

  if (document.querySelectorAll('.subscription-price-slides').length > 0) {
    const SubscriptionPriceSlides = DynamicComponent({
      loader: () => import('./Components/SubscriptionPriceSlides'),
    });
  
    Array.from(document.querySelectorAll('.subscription-price-slides')).forEach(
      (subscriptionPriceSlides, index) => {
        const blocks = subscriptionPriceSlides.querySelectorAll('.subscription-price-slides__block');
  
        if (blocks.length === 0) return;
  
        const blockname = blocks[0].dataset.blockname || ''; // <-- Extract name once
  
        const values = Array.from(blocks).map(block => ({
          blocktitle: block.dataset.blocktitle,
          subscriptionpricepermonth: block.dataset.subscriptionpricepermonth,
          subscriptionpriceperyear: block.dataset.subscriptionpriceperyear,
          blocktext: block.dataset.blocktext,
          ctalink: block.dataset.ctalink,
          ctalinkExternal: block.dataset.ctalinkexternal,
          ctatext: block.dataset.ctatext,
          ispopular: block.dataset.ispopular,
          iscolored: block.dataset.iscolored,
          usptexts: block.dataset.usptexts
        }));
  
        renderReact(
          <SubscriptionPriceSlides slides={values} blockname={blockname} />,
          subscriptionPriceSlides
        );
      }
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Containers/BuyButton.Container'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach(button => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <BuyButton
            {...{ label, articleNumber, quantityFieldId, href, cssClass }}
          />
        </Provider>,
        button,
      );
    });
  }

  if (document.querySelectorAll('reorder-button').length > 0) {
    const ReorderButton = DynamicComponent({
      loader: () => import('./Containers/ReorderButton.Container'),
    });
    Array.from(document.querySelectorAll('reorder-button')).forEach(button => {
      const { cssClass, orderRows, title } = button.dataset;
      const label = button.innerText;
      renderReact(
        <Provider store={store}>
          <ReorderButton {...{ label, title, cssClass, orderRows }} />
        </Provider>,
        button,
      );
    });
  }

  if (document.querySelectorAll('gtm-tag').length > 0) {
    Array.from(document.querySelectorAll('gtm-tag')).forEach(gtm => {
      if (gtm.dataset.click) {
        gtm.addEventListener('click', function (event) {
          pushToDatalayer(gtm.dataset);
        });
      } else {
        pushToDatalayer(gtm.dataset);
      }
    });
  }

  if (document.querySelectorAll('.comparison-list').length > 0) {
    const ComparisonList = DynamicComponent({
      loader: () => import('./Containers/Comparison/ComparisonList.container'),
    });
    Array.from(document.querySelectorAll('.comparison-list')).forEach(element => {
      renderReact(
        <Provider store={store}>
          <ComparisonList />
        </Provider>,
        element,
      );
    });
  }

  if (document.querySelectorAll('.compare-button').length > 0) {
    const CompareButton = DynamicComponent({
        loader: () => import('./Containers/Comparison/CompareButton.container'),
    });
      Array.from(document.querySelectorAll('.compare-button')).forEach(
          button => {
              const { articleNumber } = button.dataset;
              renderReact(
                  <Provider store={store}>
                      <CompareButton
                          {...{
                              articleNumber,
                          }}
                      />
                  </Provider>,
                  button,
              );
          },
      );
  }

  if (document.querySelectorAll('.comparison-holder').length > 0) {
    const ComparisonPageButton = DynamicComponent({
        loader: () => import('./Containers/Comparison/ComparisonPageButton.container'),
    });
      Array.from(document.querySelectorAll('.comparison-holder')).forEach(
          button => {
              const { href } = button.dataset;
              renderReact(
                  <Provider store={store}>
                      <ComparisonPageButton
                          {...{
                              href,
                          }}
                      />
                  </Provider>,
                  button,
              );
          },
      );
  }
  if (document.getElementById('wishlist')) {
      const WishList = DynamicComponent({
          loader: () => import('./Containers/WishList.container'),
      });
      ReactDOM.render(
          <Provider store={store}>
              <WishList />
          </Provider>,
          document.getElementById('wishlist'),
      );
  }
  if (document.getElementById('globalNotification')) {
      ReactDOM.render(
          <Provider store={store}>
              <NotificationContainer />
          </Provider>,
          document.getElementById('globalNotification'),
      );
  }
};

bootstrapComponents();

window.onload = function () {
  filterCustomScripts();
  customScripts();
  openTab();
};

window.onscroll = function () {
  stickyRow();
};
